import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import {
  FileData,
  FileSelection,
  FileSelectionState,
  GoogleFontIcon,
} from '@asset-metrix/portal-ui';
import { FileSection } from '@asset-metrix/portal-ui';
import { JsonError } from '@sagold/react-json-editor';

import { MetaDataInputField } from './MetaDataInputField';
import { GenerateSchemaFromMetaDataExtraction } from './useGenerateSchemaForMetaDataExtraction';

import styles from './doc-upload-modal.module.scss';

const DisplayUploadProgress = ({
  fileState,
}: {
  fileState: FileSelectionState;
}) => {
  const { t } = useTranslation();

  const metaDataExtractionInfo = useMemo(() => {
    const successCount = fileState.filter(
      (f) => f.uploadState === 'isSuccess'
    ).length;
    const problemCount = fileState.filter(
      (f) => f.uploadState === 'isError'
    ).length;
    const totalFiles = fileState.length;

    if (successCount > 0 || problemCount > 0) {
      const progressMessage = t('app.upload.info.progress', '', {
        count: successCount,
        total: totalFiles,
      });

      const problemMessage = problemCount
        ? t('app.upload.info.problemCount', '', {
            count: problemCount,
            total: totalFiles,
          })
        : '';

      return (
        <div className={styles['upload-info']}>
          <GoogleFontIcon name="info" solid />
          {progressMessage} {problemMessage}
        </div>
      );
    }

    return null;
  }, [fileState, t]);

  return metaDataExtractionInfo;
};

export const RenderMetaDataExtractedView = ({
  docMetadataExtractionInfo,
  documentMetaDataExtraction,
  fileState,
  onEditorDataChange,
  setErrors,
  onFileRemove,
  upload,
}: {
  docMetadataExtractionInfo: GenerateSchemaFromMetaDataExtraction;
  documentMetaDataExtraction?: FileUploadMetaDataExtractionDto;
  fileState: FileSelectionState;
  onEditorDataChange: (fileId: string, data: Record<string, unknown>) => void;
  setErrors: (fileId: string, error: JsonError[]) => void;
  onFileRemove: (file: FileData) => void;
  upload: (file: FileSelection) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const isPending = useMemo(
    () => fileState.some((selection) => selection.uploadState === 'isPending'),
    [fileState]
  );
  const { jsonSchema, dropDownOptions, inputLabel } = docMetadataExtractionInfo;

  const generateMetaDataExtractionInput = useMemo(
    () => (fileData: FileSelection) => {
      if (!dropDownOptions || !jsonSchema) {
        return;
      }

      return (
        <MetaDataInputField
          jsonSchema={jsonSchema}
          file={fileData}
          onEditorDataChange={(data) => onEditorDataChange(fileData.id, data)}
          setErrors={setErrors}
          documentMetaDataExtraction={documentMetaDataExtraction}
          dropDownOptions={dropDownOptions}
        />
      );
    },
    [
      dropDownOptions,
      jsonSchema,
      setErrors,
      documentMetaDataExtraction,
      onEditorDataChange,
    ]
  );

  if (fileState.length === 0) {
    return <div>{t('app.upload.noFiles')}</div>;
  }
  return (
    <div className={styles['meta-data-extraction-wrapper']}>
      <DisplayUploadProgress fileState={fileState} />
      <FileSection
        files={fileState}
        onRemoveFile={onFileRemove}
        showUploadState={isPending}
        title={t('app.upload.extractMetaData.title')}
        uploadFile={upload}
        metaDataInputLabel={inputLabel}
        metaDataExtractionInput={generateMetaDataExtractionInput}
      />
    </div>
  );
};
